import React, { useEffect, useState } from 'react';

import { c } from 'ttag';

import {
    AuthenticationStore,
    Badge,
    Button,
    Card,
    CardFields,
    Checkbox,
    Modal,
    useAuthentication,
    useCurrentUser,
    useLoading,
    useNotifications,
} from '@freelancelabs/components';
import { clsx } from '@freelancelabs/utils';

import { recordMfaEnrolment } from '../../api/mfaEnrolment';
import TotpPairing, { TOTP_CODE_LENGTH } from '../../app/mfa/TotpPairing';

const AccountPage = () => {
    const {
        fetchMfaSettings,
        setEmailMfaEnabled,
        setupTotp,
        verifyTotpSetup,
        disableTotp,
        forgotPassword,
        getAuthorizationToken,
    } = useAuthentication() as AuthenticationStore;
    const { email, emailVerified, federated } = useCurrentUser();
    const { createNotification } = useNotifications();
    const [isLoaded, setIsLoaded] = useState(false);
    const [mfaEnabled, setMfaEnabled] = useState(false);
    const [totpEnabled, setTotpEnabled] = useState(false);
    const [saving, withSaving] = useLoading();
    const [sendingReset, withSendingReset] = useLoading();

    const [totpSetup, setTotpSetup] = useState<{ secret: string; uri: string } | null>(null);
    const [totpCode, setTotpCode] = useState('');
    const [totpError, setTotpError] = useState('');
    const [enrolling, withEnrolling] = useLoading();

    useEffect(() => {
        const run = async () => {
            try {
                const settings = await fetchMfaSettings();
                setMfaEnabled(settings.email);
                setTotpEnabled(settings.totp);
            } finally {
                setIsLoaded(true);
            }
        };
        void run();
    }, []);

    const handleMfaToggle = (enabled: boolean) => {
        const run = async () => {
            await setEmailMfaEnabled(enabled);
            setMfaEnabled(enabled);
            createNotification({
                type: 'success',
                text: enabled
                    ? c('Mfa notification').t`Two-factor authentication enabled.`
                    : c('Mfa notification').t`Two-factor authentication disabled.`,
            });
        };
        withSaving(run()).catch(() => {
            createNotification({
                type: 'danger',
                text: c('Mfa notification').t`Unable to update two-factor authentication.`,
            });
        });
    };

    const handleTotpStart = () => {
        if (!email) {
            return;
        }
        setTotpCode('');
        setTotpError('');
        withEnrolling(setupTotp(email))
            .then(setTotpSetup)
            .catch(() => {
                createNotification({
                    type: 'danger',
                    text: c('Totp notification').t`Unable to start the authenticator app setup.`,
                });
            });
    };

    const handleTotpVerify = () => {
        if (enrolling || totpCode.length < TOTP_CODE_LENGTH) {
            return;
        }
        setTotpError('');
        withEnrolling(verifyTotpSetup(totpCode))
            .then(async () => {
                setTotpSetup(null);
                setTotpEnabled(true);
                // Records the first enrolment; silent by design, the factor is set in the pool regardless.
                const token = await getAuthorizationToken();
                if (token) {
                    recordMfaEnrolment(token);
                }
                createNotification({
                    type: 'success',
                    text: c('Totp notification').t`Authenticator app enabled.`,
                });
            })
            .catch(() => {
                setTotpError(c('Mfa error').t`Invalid code.`);
                setTotpCode('');
            });
    };

    const handleTotpDisable = () => {
        withSaving(disableTotp())
            .then(() => {
                setTotpEnabled(false);
                // Nothing to record on removal: state is read from the pool, and the enrolment date is never erased.
                createNotification({
                    type: 'success',
                    text: c('Totp notification').t`Authenticator app disabled.`,
                });
            })
            .catch(() => {
                createNotification({
                    type: 'danger',
                    text: c('Mfa notification').t`Unable to update two-factor authentication.`,
                });
            });
    };

    const handlePasswordReset = () => {
        if (!email || sendingReset) {
            return;
        }
        withSendingReset(forgotPassword(email))
            .then(() => {
                createNotification({
                    type: 'success',
                    text: c('Account notification').t`A password reset email has been sent.`,
                });
            })
            .catch((error: Error) => {
                // Cognito per-user anti-abuse rate limit.
                const isRateLimited = error?.name === 'LimitExceededException';
                createNotification({
                    type: 'danger',
                    text: isRateLimited
                        ? c('Account notification').t`Too many attempts, please try again in a few minutes.`
                        : c('Account notification').t`Unable to send the reset email.`,
                });
            });
    };

    const protectedNow = totpEnabled || mfaEnabled;
    // Both methods can be on at once: Cognito holds two independent preferences.
    const methodLabel =
        totpEnabled && mfaEnabled
            ? c('Security state').t`Authenticator app and email code`
            : totpEnabled
              ? c('Security state').t`Authenticator app`
              : mfaEnabled
                ? c('Security state').t`Email code`
                : c('Security state').t`No second step yet`;

    // Email reset stays available with TOTP; Cognito blocks it only when email itself is the second factor.
    const resetBlockedByMfa = mfaEnabled && !totpEnabled;

    // Check federated before the claim: SSO sessions carry email_verified false.
    const vouchedFor = federated
        ? c('Account field').t`Verified by your company`
        : emailVerified === true
          ? c('Account field').t`Verified`
          : null;

    return (
        <div className="flex flex-1 flex-col px-6 pt-8 pb-12">
            <div className="fl-page-body flex flex-1 flex-col gap-4">
                <header className="fl-page-body__head">
                    <h1 className="fl-page-title">{c('Account title').t`My profile`}</h1>
                    <p className="mt-1 text-xs text-text-500">
                        {c('Account title').t`Your identity and how you sign in.`}
                    </p>
                </header>

                <Card title={c('Account section').t`Identification`}>
                    <CardFields
                        className="fl-card__fields--single mt-5"
                        fields={[
                            {
                                label: c('Account field').t`Sign-in email`,
                                value: email ? (
                                    <span className="flex flex-wrap items-center gap-2">
                                        {email}
                                        {vouchedFor && (
                                            <Badge category="success" size="small">
                                                {vouchedFor}
                                            </Badge>
                                        )}
                                    </span>
                                ) : undefined,
                            },
                        ]}
                    />

                    {/* Only the sign-in email is served here; role/company/function would need
                        client-hub-api, and must not be rendered as empty dashes. */}
                </Card>

                <Card title={c('Security title').t`Two-step verification`}>
                    <p className="text-xs text-text-500">
                        {c('Security title')
                            .t`A second proof of identity is asked for at each login. Both methods can be on at once.`}
                    </p>

                    {/* While the pool read is pending, show neither "on" nor "none". */}
                    <div className="fl-setting-state mt-3">
                        {!isLoaded ? (
                            <span className="text-text-600">{c('Security state').t`Reading your settings…`}</span>
                        ) : (
                            <>
                                <Badge size="small" category={protectedNow ? 'success' : 'default'}>
                                    {protectedNow ? c('Security state').t`On` : c('Security state').t`Off`}
                                </Badge>
                                <b>{methodLabel}</b>
                                {mfaEnabled && !totpEnabled && email && (
                                    <span className="text-xs text-text-600">{email}</span>
                                )}
                            </>
                        )}
                    </div>

                    <div className="mt-3 flex flex-col gap-3">
                        <div className={clsx('fl-setting-row', totpEnabled && 'fl-setting-row--active')}>
                            <span className="fl-setting-row__text">
                                <b className="fl-setting-row__title">
                                    {c('Totp section').t`Authenticator app (TOTP)`}
                                    {!totpEnabled && (
                                        <Badge size="small" category="info">
                                            {c('Totp section').t`Recommended`}
                                        </Badge>
                                    )}
                                </b>
                                <span className="fl-setting-row__help">
                                    {c('Totp section help')
                                        .t`Recommended method: at each login, enter the code generated by your authenticator app (Google Authenticator, Microsoft Authenticator, FreeOTP...).`}
                                </span>
                            </span>
                            {totpEnabled ? (
                                <Button
                                    category="ghost"
                                    onClick={handleTotpDisable}
                                    loading={saving}
                                    disabled={!isLoaded}
                                >
                                    {c('Action').t`Disable`}
                                </Button>
                            ) : (
                                <Button onClick={handleTotpStart} loading={enrolling} disabled={!isLoaded || !email}>
                                    {c('Action').t`Enable`}
                                </Button>
                            )}
                        </div>

                        <div className={clsx('fl-setting-row', mfaEnabled && 'fl-setting-row--active')}>
                            <span className="fl-setting-row__text">
                                <b className="fl-setting-row__title">{c('Mfa section').t`Email verification code`}</b>
                                <span className="fl-setting-row__help">
                                    {c('Mfa section help')
                                        .t`Add an extra layer of security: at each login, a verification code will be sent to your email address.`}
                                </span>
                            </span>
                            <Checkbox
                                id="email-mfa"
                                checked={mfaEnabled}
                                disabled={!isLoaded}
                                loading={saving}
                                onChange={(event) => handleMfaToggle(event.target.checked)}
                                label={c('Mfa toggle').t`Enable`}
                            />
                        </div>
                    </div>
                </Card>

                <Card title={c('Account section').t`Password`}>
                    {/* No in-session password change is exposed, only sending a reset link. */}
                    <div className="fl-setting-row">
                        <span className="fl-setting-row__text">
                            <b className="fl-setting-row__title">{c('Account section').t`Change my password`}</b>
                            <span className="fl-setting-row__help">
                                {resetBlockedByMfa
                                    ? // Cognito: email cannot be both second factor and recovery channel.
                                      c('Account help')
                                          .t`Unavailable while the email verification code is enabled: turn it off for a moment to reset your password by email.`
                                    : c('Account help').t`We will send you an email to set a new password.`}
                            </span>
                        </span>
                        <Button
                            category="ghost"
                            onClick={handlePasswordReset}
                            loading={sendingReset}
                            disabled={!email || resetBlockedByMfa}
                        >
                            {c('Action').t`Reset my password`}
                        </Button>
                    </div>
                </Card>
            </div>

            <Modal
                open={Boolean(totpSetup)}
                onClose={() => setTotpSetup(null)}
                title={c('Totp title').t`Set up your authenticator app`}
                size={600}
                panelProps={{ className: 'modal_panel-container mfa-modal' }}
            >
                {totpSetup && (
                    <form
                        name="totp-setup-form"
                        onSubmit={(event) => {
                            event.preventDefault();
                            handleTotpVerify();
                        }}
                        method="post"
                    >
                        <TotpPairing
                            uri={totpSetup.uri}
                            secret={totpSetup.secret}
                            code={totpCode}
                            onCode={setTotpCode}
                            error={totpError}
                            busy={enrolling}
                        />
                        <div className="mfa-actions">
                            <Button
                                category="ghost"
                                type="button"
                                onClick={() => setTotpSetup(null)}
                                disabled={enrolling}
                            >
                                {c('Action').t`Cancel`}
                            </Button>
                            <Button type="submit" loading={enrolling} disabled={totpCode.length < TOTP_CODE_LENGTH}>
                                {c('Action').t`Verify`}
                            </Button>
                        </div>
                    </form>
                )}
            </Modal>
        </div>
    );
};

export default AccountPage;
